import { Component, OnInit, ViewContainerRef } from '@angular/core';

import { IdleTimeoutService } from '@core/services/idle-timeout/idle-timeout.service';

import {
  createCommunicationService,
  MessageType,
  NavigationPayload,
  SyncTokenPayload,
  SyncTokenRequestedPayload,
} from '@xpw-packages/communication-service/dist/angular';

import { SignalrService } from '@core/services/signalR/signalR.service';
import { Store } from '@ngrx/store';
import { setIsIframe, setToken } from './store/audit/audit.actions';
import { getCurrentUserData } from './store/current-user/current-user.actions';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription, timer } from 'rxjs';
import { DrawService } from '@core/services/popup/drawer.service';
import { IframeNotificationService } from '@core/services/notifications/iframe-notifications.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
})
export class AppIframeComponent {
  private routerSubscription: Subscription | null = null;
  private iframeCommunicationService = createCommunicationService();
  private tokenExpirySubscription: Subscription | null = null;

  constructor(
    private idleTimeoutService: IdleTimeoutService,
    private drawerService: DrawService,
    private viewContainerRef: ViewContainerRef,
    private router: Router,
    private store: Store,
    private iframeNotificationService: IframeNotificationService,
  ) {
    // add class to body
    document.body.classList.add('app-iframe');

    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('state');
    this.store.dispatch(setIsIframe({ isIframe: true }));
    if (token) {
      this.store.dispatch(setIsIframe({ isIframe: true }));
      this.store.dispatch(setToken({ token }));
      this.store.dispatch(getCurrentUserData());
      this.setupTokenExpiryTimer(token);
    }
    this.drawerService.setViewContainerRef(this.viewContainerRef);

    this.iframeCommunicationService.on<SyncTokenPayload>(
      MessageType.TOKEN_SYNC,
      (payload) => {
        console.log('Received TOKEN_SYNC request from Next.js:', payload.token);
        this.store.dispatch(setToken({ token: payload.token }));
        this.setupTokenExpiryTimer(payload.token);
        // this.store.dispatch(getCurrentUserData());
      },
    );

    this.iframeCommunicationService.on<NavigationPayload>(
      MessageType.NAVIGATION,
      (payload) => {
        console.log('Received navigation request from Next.js:', payload);
        if (payload.route) {
          this.router.navigate([payload.route], {
            queryParams: payload.params,
          });
        }
      },
    );

    // Send current route to Next.js when navigation happens
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: any) => {
        this.iframeCommunicationService.navigate(
          event.urlAfterRedirects,
          'angular',
        );
      });

    // Notify Next.js that Angular app is loaded
    this.iframeCommunicationService.dispatchAction('ANGULAR_LOADED', 'angular');
    this.iframeCommunicationService.sendMessage<SyncTokenRequestedPayload>(
      MessageType.TOKEN_SYNC_REQUESTED,
      {},
      'angular',
    );
  }

  private setupTokenExpiryTimer(token: string): void {
    // Clear any existing subscription
    if (this.tokenExpirySubscription) {
      this.tokenExpirySubscription.unsubscribe();
      this.tokenExpirySubscription = null;
    }

    // Parse the JWT token to get expiry time
    const tokenPayload = this.parseJwt(token);
    if (!tokenPayload || !tokenPayload.exp) {
      console.error('Invalid token or missing expiry');
      return;
    }

    // Get expiry time in milliseconds
    const expiryTime = tokenPayload.exp * 1000; // Convert seconds to milliseconds
    const currentTime = Date.now();
    const timeUntilExpiry = expiryTime - currentTime;

    // Set a buffer time (e.g., 5 minutes before expiry)
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    const timeUntilRefresh = timeUntilExpiry - bufferTime;

    if (timeUntilRefresh <= 0) {
      // Token is already near expiry or expired, request new token immediately
      this.refreshToken();
    } else {
      // Set a timer to refresh the token before it expires
      this.tokenExpirySubscription = timer(timeUntilRefresh).subscribe(() => {
        this.refreshToken();
      });
    }
  }

  private parseJwt(token: string): any {
    try {
      // The token is in format: header.payload.signature
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        window
          .atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      console.error('Error parsing JWT token:', e);
      return null;
    }
  }

  private refreshToken(): void {
    console.log('Token is about to expire, requesting new token');
    // Request a new token from Next.js
    this.iframeCommunicationService.sendMessage<SyncTokenRequestedPayload>(
      MessageType.TOKEN_SYNC_REQUESTED,
      {},
      'angular',
    );
  }

  ngOnDestroy(): void {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }

    if (this.tokenExpirySubscription) {
      this.tokenExpirySubscription.unsubscribe();
    }
  }
}
